import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  User,
  UserPlus,
  Mail,
  Phone,
  Check,
  ChevronDown,
  ChevronsUpDown,
  MapPin,
  Landmark,
  PenLine,
  RefreshCw,
  Search,
  TriangleAlert,
  Info,
  type LucideIcon,
} from 'lucide-react';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Switch } from '@/components/atoms/switch';
import { GooglePlacesInput } from '@/components/molecules/GooglePlacesInput';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import { FormAccordionCard, FormSectionHeader } from '@/components/molecules';
import { CompanyLocationSelector } from '@/components/organisms/CompanyLocationSelector';
import { ContactCreateDialog } from '@/components/organisms/contacts/ContactCreateDialog';
import type { ContactCreatePrefill } from '@/components/organisms/contacts/ContactFormPanel';
import { useAdditionalContacts } from '@/hooks/contacts/useAdditionalContacts';
import { useBillTo } from '@/hooks/contacts/useBillTo';
import { useContactAddresses } from '@/hooks/contacts/useContactAddresses';
import { useApiQuery } from '@/hooks/useApiQuery';
import { addressesDiffer } from '@/lib/address-match';
import { apiGet, operationsClient } from '@/lib/api';
import { getCommercialPrimaryContact } from '@/lib/contacts/commercialContactPerson';
import { formatPhoneDisplay } from '@/lib/contacts/phone';
import { firstAddressError, type AddressFieldErrors } from '@/lib/address-form-validation';
import {
  billingAddressForCompare,
  serviceAddressForCompare,
  type JobAddressSelection,
} from '@/lib/jobs/jobAddressSources';
import { POSTAL_CODE_MAX_LENGTH } from '@/lib/postal-code';
import { cn } from '@/lib/utils';
import { ROLE_LABEL, useLocationContacts } from '@/hooks/locations/useLocationContacts';
import { AddLocationContactDialog } from '@/components/organisms/locations/AddLocationContactDialog';
import {
  IrisMark,
  irisFieldClass,
  type IrisFieldMark,
} from '@/components/molecules/IrisFilledMark';
import type {
  AdditionalContact,
  BillToOverrideAddress,
  ContactAddress as ContactAddressRow,
  ContactEmail,
  ContactPhoneNumber,
} from '@/types/contact';

interface EstimateAddress {
  title?: string | null;
  label?: string | null;
  /** The location row this address came from, when it is a saved one. */
  id?: string | null;
  street?: string;
  street2?: string | null;
  city?: string;
  state?: string;
  zip?: string;
  // Saved contact-address rows carry a country and the same-as-service
  // comparison reads it, so it has to be declared rather than smuggled through
  // as an excess property.
  country?: string | null;
  isPrimary?: boolean;
  isBilling?: boolean;
  isService?: boolean;
  accessNotes?: string | null;
}

interface ContactResultAddress {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  isPrimary?: boolean;
  isBilling?: boolean;
  isService?: boolean;
  accessNotes?: string;
}

interface SelectedCustomer {
  id: string;
  name: string;
  primaryEmail?: string;
  primaryPhone?: string;
  address?: string;
}

interface ContactResult {
  _id?: string;
  id?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  primaryEmail?: string;
  primaryPhone?: string;
  addresses?: ContactResultAddress[];
}

interface JobCustomerContactsSectionProps {
  open: boolean;
  onToggle: () => void;
  bare?: boolean;
  compactWhenSelected?: boolean;
  newCustomerPresentation?: 'dialog' | 'drawer';
  /**
   * Prefill for the New customer dialog.
   *
   * With no contact chosen, this section shows a search and nothing else —
   * there is no name box, and the service-address fields are not mounted. So a
   * caller who is not in the book has nowhere for their details to land, and
   * writing them into the form's state filled boxes that were not on screen.
   * The dialog IS where a new customer's name, phone, email and address are
   * entered, so that is where an autofill has to put them.
   */
  newCustomerSeed?: ContactCreatePrefill | null;
  /** Bump to open the dialog seeded with `newCustomerSeed`. */
  newCustomerSignal?: number;
  commercialIdentityLocked?: boolean;
  /**
   * The customer is determined by something outside this form (creating a job
   * from a project — the project owns the customer). Locks the identity
   * affordances without putting the whole section in edit mode.
   */
  customerLocked?: boolean;
  /** Shown next to the locked customer, e.g. "From project PRJ-00005". */
  customerLockedReason?: string;
  contactId: string | null;
  selectedCustomer: SelectedCustomer | null;
  membershipInfo?: {
    planName: string | null;
    discountType: string | null;
    discountValue: number;
    discountTiers?: unknown[];
  } | null;
  /**
   * Set when the contact is already an active member AND a membership plan is
   * being sold on this document: the in-cart plan does not re-price (the active
   * membership wins), so the section explains why no new member savings appear.
   */
  inCartOverriddenByActive?: {
    activePlanName: string;
    inCartPlanName: string | null;
  } | null;
  billToId?: string | null;
  onBillToIdChange?: (billToId: string | null) => void;
  showBillTo?: boolean;
  /**
   * Whether this document names someone to meet on site.
   *
   * A job does: a technician is going to an address and needs to know who opens
   * the door. An estimate or a proposal is a document about a place, addressed
   * to the customer — the people at that site already live on the location
   * itself. Estimates and proposals pass `false` so the three forms ask the
   * same two questions, customer then location, and only the job asks a third.
   */
  showServiceContact?: boolean;
  /**
   * Reset billing to mirror the service address (the "Billing address same as
   * service" toggle default). Lets the owning form clear its billing selection
   * so nothing custom is submitted. Falls back to a best-effort in-component
   * reset when omitted.
   */
  onResetBillingToService?: () => void;
  customerEmail: string;
  customerPhone: string;
  /** Caller identity to prefill when creating a contact from the job. */
  customerName?: string;
  isEditMode?: boolean;
  onSelectContact: (contact: ContactResult) => void;
  onClearContact: () => void;
  onEmailChange: (email: string) => void;
  onPhoneChange: (phone: string) => void;
  contactAddresses: EstimateAddress[];
  addressMode: 'select' | 'manual';
  selectedAddressIndex: number | null;
  showAllAddresses: boolean;
  onToggleAddressMode: () => void;
  /**
   * Fields Iris filled from the call that still hold what she wrote.
   *
   * `address` covers the service-address group as a whole: the line she heard
   * is split across five boxes, so marking each one would put five orbs on one
   * row to say a single thing.
   */
  irisMarks?: IrisFieldMarks;
  onSelectServiceAddress: (index: number, address: ContactResultAddress) => void;
  onToggleShowAllAddresses: () => void;
  saStreet: string;
  saStreet2: string;
  saCity: string;
  saState: string;
  saZip: string;
  onSaStreetChange: (value: string) => void;
  onSaStreet2Change: (value: string) => void;
  onSaCityChange: (value: string) => void;
  onSaStateChange: (value: string) => void;
  onSaZipChange: (value: string) => void;
  serviceAddressErrors?: AddressFieldErrors;
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  showAllBillingAddresses: boolean;
  onToggleBillingMode: () => void;
  onSelectBillingAddress: (index: number, address: ContactResultAddress) => void;
  onToggleShowAllBillingAddresses: () => void;
  baStreet: string;
  baStreet2: string;
  baCity: string;
  baState: string;
  baZip: string;
  onBaStreetChange: (value: string) => void;
  onBaStreet2Change: (value: string) => void;
  onBaCityChange: (value: string) => void;
  onBaStateChange: (value: string) => void;
  onBaZipChange: (value: string) => void;
  billingAddressErrors?: AddressFieldErrors;
  showAddressErrors?: boolean;
  /**
   * Called with the "create a new customer" opener while no customer is
   * chosen, so the page can put that action in the section's own header
   * instead of the section repeating the word "Customer" to host it.
   */
}

const MAX_VISIBLE_ADDRESSES = 4;
const PRIMARY_CONTACT_VALUE = '__primary__';
const MANUAL_ADDRESS_VALUE = '__manual_address__';
const MANUAL_BILLING_VALUE = '__manual_billing_address__';
const SERVICE_SHOW_ALL_VALUE = '__show_all_service_addresses__';
const BILLING_SHOW_ALL_VALUE = '__show_all_billing_addresses__';
const BILL_TO_SELF_VALUE = '__bill_to_self__';
const BILL_TO_CUSTOM_VALUE = '__bill_to_custom__';

interface ContactDetail {
  firstName?: string;
  lastName?: string;
  title?: string | null;
  displayName?: string;
  companyName?: string | null;
  contactType?: string;
  email?: string;
  phone?: string;
  primaryEmail?: string | null;
  primaryPhone?: string | null;
  emails?: ContactEmail[];
  phoneNumbers?: ContactPhoneNumber[];
}

interface FieldSelectOption {
  value: string;
  label: string;
  description?: string;
  icon?: LucideIcon;
  tone?: 'default' | 'action';
  disabled?: boolean;
}

export function JobCustomerContactsSection({
  open,
  onToggle,
  bare = false,
  compactWhenSelected = false,
  newCustomerPresentation = 'dialog',
  newCustomerSeed = null,
  newCustomerSignal,
  commercialIdentityLocked = false,
  contactId,
  selectedCustomer,
  membershipInfo,
  inCartOverriddenByActive,
  billToId,
  onBillToIdChange,
  showBillTo = true,
  showServiceContact = true,
  onResetBillingToService,
  customerEmail,
  customerPhone,
  customerName = '',
  isEditMode = false,
  customerLocked = false,
  customerLockedReason,
  onSelectContact,
  onClearContact,
  onEmailChange,
  onPhoneChange,
  contactAddresses,
  addressMode,
  selectedAddressIndex,
  showAllAddresses,
  onToggleAddressMode,
  irisMarks,
  onSelectServiceAddress,
  onToggleShowAllAddresses,
  saStreet,
  saStreet2,
  saCity,
  saState,
  saZip,
  onSaStreetChange,
  onSaStreet2Change,
  onSaCityChange,
  onSaStateChange,
  onSaZipChange,
  serviceAddressErrors,
  billingMode,
  selectedBillingIndex,
  showAllBillingAddresses,
  onToggleBillingMode,
  onSelectBillingAddress,
  onToggleShowAllBillingAddresses,
  baStreet,
  baStreet2,
  baCity,
  baState,
  baZip,
  onBaStreetChange,
  onBaStreet2Change,
  onBaCityChange,
  onBaStateChange,
  onBaZipChange,
  billingAddressErrors,
  showAddressErrors = false,
}: JobCustomerContactsSectionProps) {
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerPrefill, setNewCustomerPrefill] = useState<ContactCreatePrefill>();

  const openNewCustomer = () => {
    setNewCustomerPrefill({
      name: customerName,
      phone: customerPhone,
      email: customerEmail,
      address: { street: saStreet, street2: saStreet2, city: saCity, state: saState, zip: saZip },
    });
    setShowNewCustomer(true);
  };

  // New call offers update only untouched fields in the shared contact form;
  // its initial prefill remains a mount-time snapshot.
  const newCustomerSignalRef = useRef(newCustomerSignal);
  useEffect(() => {
    if (newCustomerSignal === undefined || newCustomerSignal === newCustomerSignalRef.current) {
      return;
    }
    newCustomerSignalRef.current = newCustomerSignal;
    setNewCustomerPrefill((current) =>
      showNewCustomer
        ? {
            name: newCustomerSeed?.name ?? current?.name,
            phone: newCustomerSeed?.phone ?? current?.phone,
            email: newCustomerSeed?.email ?? current?.email,
            address: newCustomerSeed?.address
              ? { ...current?.address, ...newCustomerSeed.address }
              : current?.address,
          }
        : (newCustomerSeed ?? undefined),
    );
    setShowNewCustomer(true);
  }, [newCustomerSignal, newCustomerSeed, showNewCustomer]);

  const { data: additionalContacts } = useAdditionalContacts(contactId ?? '');
  const { data: contactDetail } = useApiQuery<ContactDetail>({
    queryKey: ['contact-detail-lite', contactId],
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(operationsClient, `/contacts/${contactId}`);
      return raw as ContactDetail;
    },
    enabled: Boolean(contactId),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    showErrorToast: false,
  });

  // Billing address follows the selected Bill To: a catalog entry resolves to
  // its override address, else the destination contact's billing address. Self
  // keeps the direct contact's billing addresses (the editable selector below).
  const { data: billToEntries = [] } = useBillTo(showBillTo ? (contactId ?? '') : '');

  const defaultBillToEntryId = billToEntries.find((entry) => entry.isDefault)?.id ?? null;
  const effectiveBillToId = billToId === undefined ? defaultBillToEntryId : billToId;
  const selectedBillToEntry = effectiveBillToId
    ? billToEntries.find((entry) => entry.id === effectiveBillToId)
    : undefined;
  const isCatalogBillTo = Boolean(selectedBillToEntry);
  const overrideAddress = selectedBillToEntry?.overrideAddress ?? null;
  const destinationContactId =
    selectedBillToEntry && !hasRealOverride(overrideAddress) ? selectedBillToEntry.contactId : '';
  const { data: destinationAddresses = [], isLoading: destinationAddressesLoading } =
    useContactAddresses(destinationContactId);
  // Name of the bill-to's destination contact, so the "no address" notice can say
  // whose address is missing. Reuses the same query key as `contactDetail` when the
  // destination is the customer itself, so it shares that cache entry.
  const { data: destinationContactDetail } = useApiQuery<ContactDetail>({
    queryKey: ['contact-detail-lite', destinationContactId],
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>>(
        operationsClient,
        `/contacts/${destinationContactId}`,
      );
      return raw as ContactDetail;
    },
    enabled: Boolean(destinationContactId),
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    showErrorToast: false,
  });
  const billToDestinationName =
    destinationContactDetail?.displayName?.trim() ||
    [destinationContactDetail?.firstName, destinationContactDetail?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim() ||
    destinationContactDetail?.companyName?.trim() ||
    selectedBillToEntry?.label?.trim() ||
    'this contact';
  const resolvedBillToAddress: BillToOverrideAddress | null = selectedBillToEntry
    ? hasRealOverride(overrideAddress)
      ? overrideAddress
      : pickBillingAddress(destinationAddresses)
    : null;

  const allEmails = contactDetail?.emails ?? [];
  const allPhones = contactDetail?.phoneNumbers ?? [];
  const subContacts: AdditionalContact[] = additionalContacts ?? [];

  const serviceAddresses = contactAddresses.filter((address) => address.isService !== false);
  const visibleServiceAddresses = showAllAddresses
    ? serviceAddresses
    : serviceAddresses.slice(0, MAX_VISIBLE_ADDRESSES);
  // Every location the customer has, not only the ones flagged `isBilling`.
  // The flag says where a bill USUALLY goes, which is worth ordering by — it
  // is not a statement that the other sites may never be billed. Filtering on
  // it meant a customer with ten locations and one flagged could be offered
  // exactly one, with no way to pick another short of typing it by hand.
  const effectiveBillingAddresses = [
    ...contactAddresses.filter((address) => address.isBilling),
    ...contactAddresses.filter((address) => !address.isBilling),
  ];
  const visibleBillingAddresses = showAllBillingAddresses
    ? effectiveBillingAddresses
    : effectiveBillingAddresses.slice(0, MAX_VISIBLE_ADDRESSES);

  // NOT-586 — the service-side twin of `selectedBillingAddress` below, and
  // deliberately the same remedy. `selectedAddressIndex` indexes ALL of the
  // contact's addresses while this picker lists only the `isService !== false`
  // ones, so a job whose row was flagged non-service after it was written points
  // at a row the list omits. Filtering the selection out resolved it to "nothing
  // selected" — an empty picker, or "No service addresses" when that row was the
  // contact's only one — while `jobFormDto` went on persisting
  // `contactAddresses[selectedAddressIndex]`.
  //
  // Dropping the selection instead of showing it would be the worse bug: it
  // sends the record back through the manual path, which carries no `addressId`
  // and no address-level tax-exemption snapshot. `isService` marks the rows a
  // contact wants dispatched to by default (its only backend invariant is
  // `isPrimary ⇒ isService`); nothing validates a saved service address against
  // it. This surfaces only the row ALREADY on the record — a user still cannot
  // newly pick a non-service row out of the list.
  //
  // Guarded on `addressMode` because this form's toggle leaves the index intact
  // when it switches to manual entry.
  const selectedServiceAddress =
    addressMode === 'manual' || selectedAddressIndex === null
      ? null
      : (contactAddresses[selectedAddressIndex] ?? null);

  // Map selectedBillingIndex (global) to effectiveBillingAddresses-relative index
  const billingSelectedIndex = (() => {
    if (selectedBillingIndex === null) return null;
    const addr = contactAddresses[selectedBillingIndex];
    const idx = effectiveBillingAddresses.indexOf(addr);
    return idx >= 0 ? idx : null;
  })();
  // NOT-586 — `selectedBillingIndex` indexes ALL of the contact's addresses,
  // while this picker may list them in a different order, so an edited job
  // can point it at a row the list leaves out. Resolving that to "nothing
  // selected" put an empty picker in front of the operator while `jobFormDto`
  // went on persisting `contactAddresses[selectedBillingIndex]` — the screen
  // and the saved job named different billing addresses. Falling back to the
  // raw row shows exactly what will be written; it is the selection, whether or
  // not the filtered list happens to offer it.
  const selectedBillingAddress =
    billingSelectedIndex !== null
      ? effectiveBillingAddresses[billingSelectedIndex]
      : selectedBillingIndex !== null
        ? (contactAddresses[selectedBillingIndex] ?? null)
        : null;

  function handleSubContactChange(value: string) {
    if (value === PRIMARY_CONTACT_VALUE) {
      onEmailChange(
        allEmails.find((e) => e.isPrimary)?.email ??
          allEmails[0]?.email ??
          contactDetail?.primaryEmail ??
          '',
      );
      onPhoneChange(
        allPhones.find((p) => p.isPrimary)?.number ??
          allPhones[0]?.number ??
          contactDetail?.primaryPhone ??
          '',
      );
      return;
    }
    const sub = subContacts.find((c) => c.id === value);
    if (!sub) return;
    onEmailChange(sub.email ?? '');
    onPhoneChange(sub.phone ?? '');
  }

  const currentSubContactId = (() => {
    if (!subContacts.length) return null;
    const match = subContacts.find(
      (c) => (c.email && c.email === customerEmail) || (c.phone && c.phone === customerPhone),
    );
    return match?.id ?? null;
  })();
  const isSubSelected = Boolean(currentSubContactId);
  // The on-site contact row appears once a customer is selected. Email/phone show
  // as read-only text beneath it; a sub-contact carries one email/phone, while the
  // primary may carry several (multiple values → a compact inline picker).
  const showContactRow = showServiceContact && Boolean(contactId);

  const contactPersonValue = currentSubContactId ?? PRIMARY_CONTACT_VALUE;
  const selectedSubContact = subContacts.find((contact) => contact.id === contactPersonValue);
  const commercialPrimary = getCommercialPrimaryContact(contactDetail);
  const primaryPersonName = commercialPrimary?.name ?? selectedCustomer?.name ?? 'Primary contact';
  const primaryPersonDescription = commercialPrimary?.title
    ? `${commercialPrimary.title} · Primary contact`
    : 'Primary contact';
  const contactPersonOptions: FieldSelectOption[] = [
    {
      value: PRIMARY_CONTACT_VALUE,
      label: primaryPersonName,
      description: primaryPersonDescription,
      icon: User,
    },
    ...subContacts.map((contact) => ({
      value: contact.id,
      label: contact.name,
      description: contact.title ?? undefined,
      icon: User,
    })),
  ];

  const selectedEmail = allEmails.find((email) => email.email === customerEmail) ?? allEmails[0];
  const emailOptions: FieldSelectOption[] = allEmails.map((email) => ({
    value: email.email,
    label: email.email,
    description: email.label ? email.label.toLowerCase() : email.isPrimary ? 'primary' : undefined,
    icon: Mail,
  }));

  const selectedPhone =
    allPhones.find((phoneNumber) => phoneNumber.number === customerPhone) ?? allPhones[0];

  // The estimate's customer snapshot can be blank (contact had no email/phone
  // at create) while the linked contact has since gained one. The display shows
  // `selectedEmail`/`selectedPhone` (from the live contact), but the committed
  // form value stays the stale blank — so what's shown isn't what saves, and
  // Send stays blocked. Commit the displayed value so WYSIWYG and the snapshot
  // refreshes on save.
  useEffect(() => {
    if (!contactId) return;
    if (!customerEmail.trim() && selectedEmail?.email) onEmailChange(selectedEmail.email);
  }, [contactId, customerEmail, selectedEmail, onEmailChange]);
  useEffect(() => {
    if (!contactId) return;
    if (!customerPhone.trim() && selectedPhone?.number) onPhoneChange(selectedPhone.number);
  }, [contactId, customerPhone, selectedPhone, onPhoneChange]);

  const phoneOptions: FieldSelectOption[] = allPhones.map((phoneNumber) => ({
    value: phoneNumber.number,
    label: formatPhoneDisplay(phoneNumber.number),
    description: phoneNumber.label
      ? phoneNumber.label.toLowerCase()
      : phoneNumber.isPrimary
        ? 'primary'
        : undefined,
    icon: Phone,
  }));

  const serviceSourceValue =
    addressMode === 'manual'
      ? MANUAL_ADDRESS_VALUE
      : selectedServiceAddress
        ? `service:${contactAddresses.indexOf(selectedServiceAddress)}`
        : '';
  /**
   * The selected row, when the visible list does not offer it — the service-side
   * twin of `unofferedBillingOption`, and there for the same reason: fixing the
   * summary alone leaves the picker itself one layer down, matching no option
   * and rendering nothing checked.
   */
  const unofferedServiceOption: FieldSelectOption[] =
    selectedServiceAddress && !visibleServiceAddresses.includes(selectedServiceAddress)
      ? [
          {
            value: `service:${contactAddresses.indexOf(selectedServiceAddress)}`,
            label: formatContactAddressOption(selectedServiceAddress),
            description: formatContactAddressCity(selectedServiceAddress),
            icon: MapPin,
          },
        ]
      : [];
  const serviceAddressOptions: FieldSelectOption[] = [
    ...unofferedServiceOption,
    ...visibleServiceAddresses.map((address) => ({
      value: `service:${contactAddresses.indexOf(address)}`,
      label: formatContactAddressOption(address),
      description: formatContactAddressCity(address),
      icon: MapPin,
    })),
    ...(serviceAddresses.length > MAX_VISIBLE_ADDRESSES
      ? [
          {
            value: SERVICE_SHOW_ALL_VALUE,
            label: showAllAddresses ? 'Show fewer addresses' : 'Show all addresses',
            description: `${serviceAddresses.length} addresses on file`,
            icon: ChevronsUpDown,
            tone: 'action' as const,
          },
        ]
      : []),
    {
      value: MANUAL_ADDRESS_VALUE,
      label: 'Enter manually',
      description: 'Use a one-off service address',
      icon: PenLine,
    },
  ];

  function handleServiceSourceSelect(value: string) {
    if (value === SERVICE_SHOW_ALL_VALUE) {
      onToggleShowAllAddresses();
      return;
    }
    if (value === MANUAL_ADDRESS_VALUE) {
      if (addressMode !== 'manual') onToggleAddressMode();
      return;
    }
    const globalIndex = Number(value.replace('service:', ''));
    const address = contactAddresses[globalIndex];
    if (Number.isFinite(globalIndex) && address) {
      if (addressMode === 'manual') onToggleAddressMode();
      onSelectServiceAddress(globalIndex, toSelectionAddress(address));
    }
  }

  /**
   * The selected row, when the visible list does not offer it.
   *
   * `billingAddressValue` names the row the job will actually save, but the
   * options are built from `visibleBillingAddresses` — so on an edited job whose
   * billing row the picker's own filter excludes, the value matched no option
   * and the trigger rendered empty again. Fixing the summary alone left this one
   * layer down. Same shape as `leadSourceOptions`: carry the off-list value so a
   * selection always has something to point at.
   */
  const unofferedBillingOption: FieldSelectOption[] =
    selectedBillingAddress && !visibleBillingAddresses.includes(selectedBillingAddress)
      ? [
          {
            value: `billing:${contactAddresses.indexOf(selectedBillingAddress)}`,
            label: formatContactAddressOption(selectedBillingAddress),
            description: formatContactAddressCity(selectedBillingAddress),
            icon: MapPin,
          },
        ]
      : [];
  const flatBillingSourceValue =
    billingMode === 'manual'
      ? MANUAL_BILLING_VALUE
      : selectedBillingAddress
        ? `billing:${contactAddresses.indexOf(selectedBillingAddress)}`
        : '';
  const flatBillingAddressOptions: FieldSelectOption[] = [
    ...unofferedBillingOption,
    ...visibleBillingAddresses.map((address) => ({
      value: `billing:${contactAddresses.indexOf(address)}`,
      label: formatContactAddressOption(address),
      description: formatContactAddressCity(address),
      icon: MapPin,
    })),
    ...(effectiveBillingAddresses.length > MAX_VISIBLE_ADDRESSES
      ? [
          {
            value: BILLING_SHOW_ALL_VALUE,
            label: showAllBillingAddresses ? 'Show fewer addresses' : 'Show all addresses',
            description: `${effectiveBillingAddresses.length} addresses on file`,
            icon: ChevronsUpDown,
            tone: 'action' as const,
          },
        ]
      : []),
    {
      value: MANUAL_BILLING_VALUE,
      label: 'Enter manually',
      description: 'Use a one-off billing address',
      icon: PenLine,
    },
  ];

  function handleFlatBillingSourceSelect(value: string) {
    if (value === BILLING_SHOW_ALL_VALUE) {
      onToggleShowAllBillingAddresses();
      return;
    }
    if (value === MANUAL_BILLING_VALUE) {
      if (billingMode !== 'manual') onToggleBillingMode();
      return;
    }
    const globalIndex = Number(value.replace('billing:', ''));
    const address = contactAddresses[globalIndex];
    if (Number.isFinite(globalIndex) && address) {
      if (billingMode === 'manual') onToggleBillingMode();
      onSelectBillingAddress(globalIndex, toSelectionAddress(address));
    }
  }

  const billToValue =
    billingMode === 'manual' && !effectiveBillToId
      ? BILL_TO_CUSTOM_VALUE
      : effectiveBillToId
        ? effectiveBillToId
        : BILL_TO_SELF_VALUE;
  // Three answers to "where does the invoice go?", and only three: the customer
  // themselves, a one-off address typed here, or one of the customer's own
  // locations. The saved bill-to catalog is not offered — a location IS the
  // saved relationship, and keeping a second list of billing parties alongside
  // the locations model gave two places to say the same thing.
  const billToOptions: FieldSelectOption[] = [
    ...unofferedBillingOption,
    {
      value: BILL_TO_SELF_VALUE,
      label: 'Self',
      description: 'This contact',
      icon: Landmark,
    },
    {
      value: BILL_TO_CUSTOM_VALUE,
      label: 'Custom',
      description: 'Enter a one-off billing address',
      icon: PenLine,
    },
    ...visibleBillingAddresses.map((address) => ({
      value: `billing:${contactAddresses.indexOf(address)}`,
      label: formatContactAddressOption(address),
      description: formatContactAddressCity(address) || 'Location',
      icon: MapPin,
    })),
    ...(effectiveBillingAddresses.length > MAX_VISIBLE_ADDRESSES
      ? [
          {
            value: BILLING_SHOW_ALL_VALUE,
            label: showAllBillingAddresses ? 'Show fewer locations' : 'Show all locations',
            description: `${effectiveBillingAddresses.length} locations on file`,
            icon: ChevronsUpDown,
            tone: 'action' as const,
          },
        ]
      : []),
  ];

  function handleBillToSelect(value: string) {
    if (value === BILLING_SHOW_ALL_VALUE) {
      onToggleShowAllBillingAddresses();
      return;
    }
    if (value === BILL_TO_CUSTOM_VALUE) {
      onBillToIdChange?.(null);
      if (billingMode !== 'manual') onToggleBillingMode();
      return;
    }
    if (value === BILL_TO_SELF_VALUE) {
      onBillToIdChange?.(null);
      if (billingMode === 'manual') onToggleBillingMode();
      return;
    }
    if (value.startsWith('billing:')) {
      onBillToIdChange?.(null);
      handleFlatBillingSourceSelect(value);
      return;
    }
    onBillToIdChange?.(value);
    if (billingMode === 'manual') onToggleBillingMode();
  }

  // On-site contact display: a sub-contact carries its own single email/phone;
  // the primary contact's live email/phone (multi-value → compact picker below).
  const displayEmail = isSubSelected ? customerEmail : (selectedEmail?.email ?? customerEmail);
  const displayPhoneRaw = isSubSelected ? customerPhone : (selectedPhone?.number ?? customerPhone);
  const displayPhone = displayPhoneRaw ? formatPhoneDisplay(displayPhoneRaw) : '';
  const emailPickable = !isSubSelected && allEmails.length > 1;
  const phonePickable = !isSubSelected && allPhones.length > 1;
  const contactPersonLabel = selectedSubContact ? selectedSubContact.name : primaryPersonName;
  const contactPersonSub = selectedSubContact
    ? (selectedSubContact.title ?? undefined)
    : primaryPersonDescription;

  const manualServiceLines = [
    [saStreet, saStreet2].filter(Boolean).join(', '),
    [saCity, saState, saZip].filter(Boolean).join(', '),
  ].filter(Boolean);
  const serviceLines =
    addressMode === 'manual'
      ? manualServiceLines
      : selectedServiceAddress
        ? [
            formatContactAddressStreet(selectedServiceAddress),
            formatContactAddressCity(selectedServiceAddress),
          ].filter(Boolean)
        : [];
  const servicePlaceholder = contactId
    ? serviceAddresses.length > 0
      ? 'Select an address'
      : 'No service addresses'
    : 'Select a customer first';

  const billingIsCatalog = isCatalogBillTo;
  const billingIsManual = !isCatalogBillTo && billingMode === 'manual';
  const flatIsManual = billingMode === 'manual';
  const manualBillingLines = [
    [baStreet, baStreet2].filter(Boolean).join(', '),
    [baCity, baState, baZip].filter(Boolean).join(', '),
  ].filter(Boolean);
  const selfBillingLines = selectedBillingAddress
    ? [
        formatContactAddressName(selectedBillingAddress),
        formatContactAddressStreet(selectedBillingAddress),
        formatContactAddressCity(selectedBillingAddress),
      ].filter(Boolean)
    : [];
  const resolvedCatalogBillingLines = overrideAddressLines(resolvedBillToAddress);
  // A selected bill-to whose destination has no address on file (and no override)
  // resolves to zero lines, which the trigger/summary would otherwise render as
  // "not selected" — hiding the fact that a bill-to IS chosen. Surface a short
  // note instead so the empty address is explicit rather than silently null.
  const catalogBillingLines =
    isCatalogBillTo && resolvedCatalogBillingLines.length === 0 && !destinationAddressesLoading
      ? [billToDestinationName, 'No address on file']
      : resolvedCatalogBillingLines;

  // Bill-to contact — its own picker (primary + additional contacts), driven by
  // `billToContactId` and independent of the on-site service contact above.

  // Where the invoice goes. It follows the CUSTOMER, not the location being
  // worked on: a company with ten sites is still billed at its own address, and
  // picking the site to visit is not a statement about where the bill is sent.
  // So there is no "same as service" toggle to get wrong — the billing fields
  // open only when someone asks to bill somewhere else. An already-explicit
  // choice (manual entry, a saved billing address, or a catalog bill-to) keeps
  // them open, e.g. editing a job that bills a third party.
  const billingIsExplicit =
    billingMode === 'manual' ||
    selectedBillingIndex !== null ||
    // Use the EFFECTIVE bill-to (falls back to the contact's default catalog
    // entry when billToId is unset), not the raw prop. Otherwise a contact with a
    // default third-party bill-to would show "same as service" + hide the field
    // while the backend silently applies that default on create — the job bills a
    // third party the form never surfaced.
    (effectiveBillToId != null && isCatalogBillTo);
  const [billingOverrideOpen, setBillingOverrideOpen] = useState(false);
  const billingExpanded = billingOverrideOpen || billingIsExplicit;
  useEffect(() => {
    setBillingOverrideOpen(false);
  }, [contactId]);

  function handleBillElsewhereChange(billElsewhere: boolean) {
    if (billElsewhere) {
      setBillingOverrideOpen(true);
      return;
    }
    setBillingOverrideOpen(false);
    if (onResetBillingToService) {
      onResetBillingToService();
      return;
    }
    // Best-effort fallback when the owner doesn't wire a reset: drop any custom
    // bill-to and leave manual-entry mode.
    onBillToIdChange?.(null);
    if (billingMode === 'manual') onToggleBillingMode();
  }

  const effectiveServiceAddressErrors = serviceAddressErrors ?? {};
  const effectiveBillingAddressErrors = billingAddressErrors ?? {};
  const selectedServiceAddressError =
    showAddressErrors && addressMode !== 'manual'
      ? firstAddressError('Service address', effectiveServiceAddressErrors)
      : null;
  const selectedBillingAddressError =
    showAddressErrors && billingMode !== 'manual' && !billingIsCatalog
      ? firstAddressError('Billing address', effectiveBillingAddressErrors)
      : null;

  const serviceAddressFields = (
    <AddressFields
      prefix="sa"
      irisMarks={irisMarks}
      street={saStreet}
      street2={saStreet2}
      city={saCity}
      state={saState}
      zip={saZip}
      onStreetChange={onSaStreetChange}
      onStreet2Change={onSaStreet2Change}
      onCityChange={onSaCityChange}
      onStateChange={onSaStateChange}
      onZipChange={onSaZipChange}
      errors={effectiveServiceAddressErrors}
      showErrors={showAddressErrors || addressMode === 'manual'}
    />
  );
  const billingAddressFields = (
    <AddressFields
      prefix="ba"
      street={baStreet}
      street2={baStreet2}
      city={baCity}
      state={baState}
      zip={baZip}
      onStreetChange={onBaStreetChange}
      onStreet2Change={onBaStreet2Change}
      onCityChange={onBaCityChange}
      onStateChange={onBaStateChange}
      onZipChange={onBaZipChange}
      errors={effectiveBillingAddressErrors}
      showErrors={showAddressErrors || billingMode === 'manual'}
    />
  );

  const serviceContactField = showContactRow ? (
    <div className="space-y-2">
      <SummaryRow
        summary={
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{contactPersonLabel}</p>
              {contactPersonSub ? (
                <p className="truncate text-xs text-muted-foreground">{contactPersonSub}</p>
              ) : null}
            </div>
          </div>
        }
      >
        {(close) => (
          <OptionRows
            options={contactPersonOptions}
            value={contactPersonValue}
            onSelect={(value) => {
              handleSubContactChange(value);
              close();
            }}
          />
        )}
      </SummaryRow>
      {displayEmail || displayPhone ? (
        <p className="grid gap-1.5 rounded-md bg-muted/30 px-3 py-2 text-xs text-muted-foreground sm:grid-cols-2">
          {displayEmail ? (
            <MetaValue
              icon={Mail}
              text={displayEmail}
              pickable={emailPickable}
              options={emailOptions}
              value={customerEmail}
              onSelect={onEmailChange}
            />
          ) : null}
          {displayPhone ? (
            <MetaValue
              icon={Phone}
              text={displayPhone}
              pickable={phonePickable}
              options={phoneOptions}
              value={customerPhone}
              onSelect={onPhoneChange}
            />
          ) : null}
        </p>
      ) : null}
    </div>
  ) : (
    <p className="rounded-md border border-dashed border-input px-3 py-2.5 text-sm text-muted-foreground">
      Select a customer first
    </p>
  );
  const billingSummaryLines = billingExpanded
    ? billingIsCatalog
      ? catalogBillingLines
      : billingIsManual
        ? manualBillingLines
        : selfBillingLines
    : serviceLines;
  const billingSummary = billingExpanded
    ? billingSummaryLines.join(' · ') || 'Billing address not selected'
    : 'Same as service';
  // NOT-586 — `billingExpanded` is PROVENANCE: it says the billing address was
  // chosen deliberately (a saved address, a catalog bill-to, or the override
  // toggle), which is the right question for whether to open the field. It is
  // the wrong question for the warning below, which claims the two addresses
  // DIFFER — so a contact whose saved billing address named the same place got
  // an amber "Different from service address" over two identical blocks.
  //
  // Compare the addresses themselves, not the lines they render to. The two
  // sides come from different formatters — a catalog bill-to writes the postal
  // code after a space, a saved address writes it after a comma — so a
  // line-level comparison called two identical addresses different on
  // punctuation alone, and on postal spacing ("V3M0P2" vs "V3M 0P2").
  //
  // The operands come from lib/jobs/jobAddressSources, which is also what
  // jobFormDto compares to decide whether to PERSIST a billing address. Deriving
  // them here instead let the two drift: the DTO compared its country-stamped
  // output, so a Canadian job saw no warning here and still saved a redundant
  // billing address. A catalog bill-to lives outside `contactAddresses` and is
  // never persisted as the job's own billing address, so it is passed in here.
  const addressSelection: JobAddressSelection = {
    addressMode,
    selectedAddressIndex,
    saStreet,
    saStreet2,
    saCity,
    saState,
    saZip,
    billingMode,
    selectedBillingIndex,
    baStreet,
    baStreet2,
    baCity,
    baState,
    baZip,
    contactAddresses,
  };
  const serviceCompareAddress = serviceAddressForCompare(addressSelection);
  const billingCompareAddress = billingIsCatalog
    ? resolvedBillToAddress
    : billingAddressForCompare(addressSelection);
  const billingDiffersFromService =
    billingExpanded && addressesDiffer(serviceCompareAddress, billingCompareAddress);
  const compactCustomerName = selectedCustomer?.name ?? contactPersonLabel;
  // The customer's own address, which is where the bill goes unless someone
  // says otherwise. Not the location being worked on.
  const customerBillingSummary = selectedCustomer?.address?.trim() || '';
  const billingMatchesService =
    customerBillingSummary.length > 0 &&
    customerBillingSummary.replace(/\s+/g, ' ').toLowerCase() ===
      serviceLines.join(', ').replace(/\s+/g, ' ').toLowerCase();
  const billingSameSwitchId = useId();
  const shouldCompactCustomer = compactWhenSelected && Boolean(contactId);
  // Address and site contact read as plain text under the name; Edit swaps
  // them for pickable fields. Most jobs change neither, so the resting state
  // spends no boxes on them.
  const [detailsEditing, setDetailsEditing] = useState(false);
  const contactIsCustomer =
    contactPersonLabel.trim().toLowerCase() === compactCustomerName.trim().toLowerCase();

  // The section renders headerless on create, so its whole-customer actions —
  // Edit (opens the address/contact fields) and Change (swaps who the job is
  // for) — sit on the identity row itself.
  const canChangeCustomer = Boolean(contactId) && !isEditMode && !customerLocked;
  const compactBody = shouldCompactCustomer ? (
    <div className="space-y-4">
      {/* Who the job is for and where the work happens, as plain text: name,
          how to reach them, the service address, the site contact when it is
          someone other than the customer, and whether member pricing is in
          force. */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[15px] font-semibold leading-5 tracking-[-0.01em] text-foreground">
            {compactCustomerName}
          </p>
          {displayPhone || displayEmail ? (
            <p className="mt-1.5 truncate text-[13px] leading-[1.5] text-foreground/80">
              {[displayPhone, displayEmail].filter(Boolean).join('  ·  ')}
            </p>
          ) : null}
          {/* Where the work happens.
              A customer with ONE location has nothing to choose, so it stays a
              plain line. A customer with several — the whole point of the
              locations model — gets the picker right here: burying it behind
              Edit meant a job or estimate silently took whichever location
              happened to be primary. */}
          {!detailsEditing && addressMode !== 'manual' && serviceAddresses.length > 1 ? (
            <div className="mt-1.5 max-w-2xl">
              <LocationPicker
                disabled={!contactId || commercialIdentityLocked}
                value={
                  [formatContactAddressName(selectedServiceAddress), serviceLines.join(', ')]
                    .filter(Boolean)
                    .join(' — ') || servicePlaceholder
                }
                total={serviceAddresses.length}
                addresses={serviceAddresses}
                indexOf={(address) => contactAddresses.indexOf(address)}
                selectedIndex={
                  selectedServiceAddress ? contactAddresses.indexOf(selectedServiceAddress) : null
                }
                onPick={(globalIndex) => handleServiceSourceSelect(`service:${globalIndex}`)}
                onManual={() => handleServiceSourceSelect(MANUAL_ADDRESS_VALUE)}
              />
            </div>
          ) : !detailsEditing && addressMode !== 'manual' && serviceLines.length > 0 ? (
            <p className="mt-1.5 text-[13px] leading-[1.5] text-foreground/80">
              {[formatContactAddressName(selectedServiceAddress), serviceLines.join(', ')]
                .filter(Boolean)
                .join(' — ')}
            </p>
          ) : null}
          {/* Jobs only — see `LocationPeopleDisclosure`. */}
          {showServiceContact &&
          !detailsEditing &&
          addressMode !== 'manual' &&
          selectedServiceAddress?.id ? (
            <LocationPeopleDisclosure
              locationId={selectedServiceAddress.id}
              disabled={isEditMode && customerLocked}
            />
          ) : null}
          {!detailsEditing && addressMode !== 'manual' && showContactRow && !contactIsCustomer ? (
            <p className="mt-1.5 truncate text-[13px] leading-[1.5] text-foreground/80">
              Site contact: {contactPersonLabel}
            </p>
          ) : null}
          {/* Spelled out, not reduced to a plan-name chip: what the operator
              needs to know is that member pricing is in force on this job, and
              a bare plan name does not say that. */}
          {membershipInfo ? (
            <p className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium leading-tight text-success">
              <Check className="size-3.5 shrink-0" />
              <span className="min-w-0 truncate">
                Active plan: {membershipInfo.planName?.trim() || 'Membership'} · Member benefits
                applied
              </span>
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {canChangeCustomer && addressMode !== 'manual' ? (
            <button
              type="button"
              aria-pressed={detailsEditing}
              onClick={() => setDetailsEditing((editing) => !editing)}
              className={cn(
                'inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-[12px] font-semibold transition-colors',
                detailsEditing
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {detailsEditing ? <Check className="size-3" /> : <PenLine className="size-3" />}
              {detailsEditing ? 'Done' : 'Edit'}
            </button>
          ) : null}
          {canChangeCustomer ? (
            <button
              type="button"
              onClick={onClearContact}
              className="inline-flex h-7 items-center gap-1.5 rounded-full bg-muted px-3 text-[12px] font-semibold text-foreground transition-colors hover:bg-muted/70"
            >
              <RefreshCw className="size-3" />
              Change
            </button>
          ) : null}
        </div>
      </div>

      <InCartMembershipNotice info={inCartOverriddenByActive} />

      {/* Edit opens the same facts as ordinary labeled fields, on the same
          grid the rest of the form uses. If it can be changed, it looks like
          a field — and when nothing is being changed, no boxes. */}
      {addressMode === 'manual' ? (
        <div className="space-y-2">
          <Label>Service address</Label>
          {serviceAddressFields}
          <button
            type="button"
            onClick={onToggleAddressMode}
            className="text-[11.5px] font-medium text-primary hover:underline"
          >
            ← Choose a saved address
          </button>
          <AddressValidationSummary message={selectedServiceAddressError} />
        </div>
      ) : (
        <>
          {detailsEditing ? (
            <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 sm:grid-cols-2">
              <FieldPicker
                label="Service address"
                disabled={!contactId || commercialIdentityLocked}
                value={
                  [formatContactAddressName(selectedServiceAddress), serviceLines.join(', ')]
                    .filter(Boolean)
                    .join(' — ') || servicePlaceholder
                }
              >
                {(close) => (
                  <OptionRows
                    options={serviceAddressOptions}
                    value={serviceSourceValue}
                    onSelect={(value) => {
                      handleServiceSourceSelect(value);
                      if (value !== SERVICE_SHOW_ALL_VALUE) close();
                    }}
                  />
                )}
              </FieldPicker>
              {showContactRow ? (
                <FieldPicker label="Site contact" value={contactPersonLabel}>
                  {(close) => (
                    <OptionRows
                      options={contactPersonOptions}
                      value={contactPersonValue}
                      onSelect={(value) => {
                        handleSubContactChange(value);
                        close();
                      }}
                    />
                  )}
                </FieldPicker>
              ) : null}
            </div>
          ) : null}
          <AddressValidationSummary message={selectedServiceAddressError} />
        </>
      )}

      {/* Billing asks one question; the fields it opens are the answer. */}
      <label
        htmlFor={billingSameSwitchId}
        className="flex w-fit cursor-pointer items-center gap-2.5"
      >
        <span className="text-[13px] font-medium text-foreground">Bill somewhere else</span>
        <Switch
          id={billingSameSwitchId}
          checked={billingExpanded}
          disabled={!contactId}
          onCheckedChange={(checked) => handleBillElsewhereChange(checked)}
        />
      </label>
      {!billingExpanded ? (
        <p className="text-[13px] leading-[1.5] text-muted-foreground">
          Billed to {compactCustomerName || 'the customer'}
          {/* Only worth naming the address when it is somewhere else. Repeating
              the service address under the service address says nothing. */}
          {customerBillingSummary && !billingMatchesService ? ` · ${customerBillingSummary}` : ''}
        </p>
      ) : null}

      {billingExpanded ? (
        <>
          <div className="grid grid-cols-1 items-start gap-x-5 gap-y-4 sm:grid-cols-2">
            <FieldPicker
              label="Billing address"
              disabled={!contactId}
              value={
                <span data-testid="job-billing-summary">
                  {billingSummary ||
                    (billingIsManual ? 'Enter address below' : 'Select billing address')}
                </span>
              }
              sub={
                billingDiffersFromService ? (
                  <span className="inline-flex items-center gap-1 font-medium text-warning">
                    <TriangleAlert className="size-3" />
                    Different from service address
                  </span>
                ) : billingIsCatalog ? (
                  (selectedBillToEntry?.label ?? 'Bill-to')
                ) : billingIsManual ? (
                  'Custom address'
                ) : undefined
              }
            >
              {(close) =>
                showBillTo ? (
                  <div className="space-y-2">
                    <OptionRows
                      options={billToOptions}
                      value={billToValue}
                      onSelect={(value) => {
                        handleBillToSelect(value);
                        if (value !== BILLING_SHOW_ALL_VALUE) close();
                      }}
                    />
                  </div>
                ) : (
                  <OptionRows
                    options={flatBillingAddressOptions}
                    value={flatBillingSourceValue}
                    onSelect={(value) => {
                      handleFlatBillingSourceSelect(value);
                      if (value !== BILLING_SHOW_ALL_VALUE) close();
                    }}
                  />
                )
              }
            </FieldPicker>
          </div>

          {(showBillTo ? billingIsManual : flatIsManual) ? billingAddressFields : null}
        </>
      ) : null}

      {customerLocked && customerLockedReason ? (
        <p className="text-[11.5px] text-muted-foreground">{customerLockedReason}</p>
      ) : null}
    </div>
  ) : null;
  // The empty Customer section opens as a dashed slot rather than a live search
  // box; clicking it swaps in the search and puts the caret in it.
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const contactSearchRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (contactSearchOpen) contactSearchRef.current?.querySelector('input')?.focus();
  }, [contactSearchOpen]);
  const compactEmptyBody =
    compactWhenSelected && !contactId ? (
      <div className="space-y-3">
        <div className="space-y-1.5">
          {/* No label and no section header: the empty state is one row — the
                add card on the left in the header's old place, New customer at
                the section's right edge where it has always been. */}
          <div className="flex items-start justify-between gap-3">
            {contactSearchOpen || isEditMode || customerLocked ? (
              <div ref={contactSearchRef} className="min-w-0 flex-1">
                <CompanyLocationSelector
                  selected={selectedCustomer}
                  onSelect={onSelectContact}
                  onClear={onClearContact}
                  selectedLayout="inline"
                  nameOnly
                  pickBranch={false}
                  readOnly={isEditMode || customerLocked}
                  // The dropdown carries its own way out: a New-customer row
                  // at its foot, for when the search comes up dry.
                  onCreateNew={openNewCustomer}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setContactSearchOpen(true)}
                className="inline-flex h-14 w-full max-w-[260px] items-center justify-center gap-2 rounded-lg border border-dashed border-muted-foreground/45 bg-muted/40 text-sm font-medium text-foreground transition-colors hover:border-muted-foreground/70 hover:bg-muted/60"
              >
                <UserPlus className="size-4 shrink-0" />
                Add contact
              </button>
            )}
            {!isEditMode && !customerLocked ? (
              <button
                type="button"
                onClick={openNewCustomer}
                className="inline-flex shrink-0 items-center gap-1 pt-1 text-xs font-semibold text-blue-800 transition-colors hover:text-blue-900 hover:underline"
              >
                <UserPlus className="size-3.5" />
                New customer
              </button>
            ) : null}
          </div>
        </div>
      </div>
    ) : null;

  const body = (
    <>
      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        <div className="min-w-0 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-muted-foreground">Customer</p>
            {!isEditMode && !customerLocked && !contactId ? (
              <button
                type="button"
                onClick={openNewCustomer}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <UserPlus className="h-3.5 w-3.5" />
                New customer
              </button>
            ) : null}
          </div>
          <CompanyLocationSelector
            selected={selectedCustomer}
            onSelect={onSelectContact}
            onClear={onClearContact}
            selectedLayout="inline"
            nameOnly
            pickBranch={false}
            readOnly={isEditMode || customerLocked}
          />
        </div>

        {showServiceContact ? (
          <div className="min-w-0 space-y-1.5">
            <p className="text-sm font-medium text-muted-foreground">Service contact</p>
            {serviceContactField}
          </div>
        ) : null}
      </div>

      {membershipInfo ? <ActiveMembershipBanner membershipInfo={membershipInfo} /> : null}
      <InCartMembershipNotice info={inCartOverriddenByActive} />

      <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
        {/* Typing an address needs the width. Left in one half of a two-column
            grid, Street / Unit / City / State / ZIP had ~350px between them and
            came out as five stacked boxes — a column of one-word fields that
            reads nothing like an address. Picking a SAVED one is a single
            summary row and stays in its half. */}
        <div className={cn('min-w-0 space-y-1.5', addressMode === 'manual' && 'md:col-span-2')}>
          <p className="text-sm font-medium text-muted-foreground">Service address</p>
          {addressMode === 'manual' ? (
            <div className="space-y-2">
              {serviceAddressFields}
              <button
                type="button"
                onClick={onToggleAddressMode}
                className="text-xs font-medium text-primary hover:underline"
              >
                ← Choose a saved address
              </button>
            </div>
          ) : (
            <SummaryRow
              align="start"
              disabled={!contactId}
              summary={
                <AddressTrigger
                  lines={serviceLines}
                  badge={selectedServiceAddress?.isPrimary ? 'Primary' : undefined}
                  placeholder={servicePlaceholder}
                />
              }
            >
              {(close) => (
                <OptionRows
                  options={serviceAddressOptions}
                  value={serviceSourceValue}
                  onSelect={(value) => {
                    handleServiceSourceSelect(value);
                    if (value !== SERVICE_SHOW_ALL_VALUE) close();
                  }}
                />
              )}
            </SummaryRow>
          )}
          <AddressValidationSummary message={selectedServiceAddressError} />
        </div>
      </div>

      <div className="space-y-3 rounded-md border bg-muted/20 p-3">
        <label className="flex cursor-pointer items-center justify-between gap-3">
          <span className="min-w-0">
            <span className="block text-sm font-medium text-foreground">Billing address</span>
            <span className="block truncate text-xs text-muted-foreground">
              {billingExpanded
                ? billingSummary
                : customerBillingSummary || `Billed to ${compactCustomerName || 'the customer'}`}
            </span>
          </span>
          <Switch
            checked={billingExpanded}
            disabled={!contactId}
            onCheckedChange={(checked) => handleBillElsewhereChange(checked)}
          />
        </label>
        {billingExpanded ? (
          <div className="space-y-1.5">
            {showBillTo ? (
              <div className="space-y-2">
                <SummaryRow
                  align="start"
                  disabled={!contactId}
                  summary={
                    <AddressTrigger
                      lines={
                        billingIsCatalog
                          ? catalogBillingLines
                          : billingIsManual
                            ? manualBillingLines
                            : selfBillingLines
                      }
                      badge={
                        billingIsCatalog
                          ? (selectedBillToEntry?.label ?? 'Bill-to')
                          : billingIsManual
                            ? 'Custom'
                            : selectedBillingAddress?.isPrimary
                              ? 'Primary'
                              : undefined
                      }
                      placeholder={
                        billingIsManual
                          ? 'Enter address below'
                          : contactId
                            ? 'Select billing address'
                            : 'Select a customer first'
                      }
                    />
                  }
                >
                  {(close) => (
                    <div className="space-y-2">
                      <OptionRows
                        options={billToOptions}
                        value={billToValue}
                        onSelect={(value) => {
                          handleBillToSelect(value);
                          if (value !== BILLING_SHOW_ALL_VALUE) close();
                        }}
                      />
                    </div>
                  )}
                </SummaryRow>
                {billingIsManual ? billingAddressFields : null}
              </div>
            ) : (
              <div className="space-y-2">
                <SummaryRow
                  align="start"
                  disabled={!contactId}
                  summary={
                    <AddressTrigger
                      lines={flatIsManual ? manualBillingLines : selfBillingLines}
                      badge={
                        flatIsManual
                          ? 'Custom'
                          : selectedBillingAddress?.isPrimary
                            ? 'Primary'
                            : undefined
                      }
                      placeholder={
                        flatIsManual
                          ? 'Enter address below'
                          : contactId
                            ? effectiveBillingAddresses.length > 0
                              ? 'Select billing address'
                              : 'No billing addresses'
                            : 'Select a customer first'
                      }
                    />
                  }
                >
                  {(close) => (
                    <OptionRows
                      options={flatBillingAddressOptions}
                      value={flatBillingSourceValue}
                      onSelect={(value) => {
                        handleFlatBillingSourceSelect(value);
                        if (value !== BILLING_SHOW_ALL_VALUE) close();
                      }}
                    />
                  )}
                </SummaryRow>
                {flatIsManual ? billingAddressFields : null}
              </div>
            )}
            <AddressValidationSummary message={selectedBillingAddressError} />
          </div>
        ) : null}
      </div>
    </>
  );

  const customerForm = (
    <ContactCreateDialog
      presentation={newCustomerPresentation}
      open={showNewCustomer && !contactId && !isEditMode && !customerLocked}
      onOpenChange={setShowNewCustomer}
      initialValues={newCustomerPrefill}
      autofillValues={newCustomerSignal ? newCustomerPrefill : undefined}
      irisMarks={irisMarks}
      onSelectContact={(contact) =>
        onSelectContact({
          ...contact,
          primaryEmail: contact.primaryEmail ?? undefined,
          primaryPhone: contact.primaryPhone ?? undefined,
        })
      }
    />
  );

  return (
    <>
      {customerForm}
      {bare ? (
        (compactEmptyBody ?? compactBody ?? <div className="space-y-4">{body}</div>)
      ) : (
        <FormAccordionCard
          open={open}
          header={
            <FormSectionHeader icon={User} title="Customer" open={open} onToggle={onToggle} />
          }
          bodyClassName="space-y-4"
        >
          {body}
        </FormAccordionCard>
      )}
    </>
  );
}

function ActiveMembershipBanner({
  membershipInfo,
}: {
  membershipInfo: NonNullable<JobCustomerContactsSectionProps['membershipInfo']>;
}) {
  const planName = membershipInfo.planName?.trim() || 'Active plan';
  const discountLabel = formatMembershipDiscountLabel(membershipInfo);

  return (
    <div className="flex items-start gap-2 rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
      <div className="min-w-0">
        <p className="font-medium">
          Active plan: {planName} · {discountLabel}
        </p>
      </div>
    </div>
  );
}

/**
 * Explains the narrow duplicate-plan case. Distinct second memberships are valid
 * and are evaluated with the active plans instead of rendering this notice.
 */
function InCartMembershipNotice({
  info,
  className,
}: {
  info: NonNullable<JobCustomerContactsSectionProps['inCartOverriddenByActive']> | null | undefined;
  className?: string;
}) {
  if (!info) return null;
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning',
        className,
      )}
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium">This plan is already active</p>
        <p className="text-warning/90">
          {info.inCartPlanName
            ? `${info.inCartPlanName} already belongs to this customer and won’t be counted twice.`
            : `${info.activePlanName} already belongs to this customer and won’t be counted twice.`}{' '}
          Choose a different plan to add complementary benefits.
        </p>
      </div>
    </div>
  );
}

function formatMembershipDiscountLabel({
  discountType,
  discountTiers,
  discountValue,
}: NonNullable<JobCustomerContactsSectionProps['membershipInfo']>) {
  if ((discountTiers ?? []).length > 0) return 'Member benefits applied';
  if (discountValue <= 0) return 'Member benefits applied';
  const normalizedType = String(discountType ?? '')
    .trim()
    .toUpperCase();
  if (normalizedType === 'PERCENT') return `${discountValue}% off`;
  if (normalizedType === 'FLAT') return `$${discountValue.toFixed(2)} off`;
  return 'Member benefits applied';
}

/**
 * An overrideAddress counts as real only when it carries an actual address line.
 * `country` alone is a default ("US") and must not flip an otherwise-empty override
 * into a real one — mirrors the backend normalizer.
 */
function hasRealOverride(address: BillToOverrideAddress | null | undefined): boolean {
  if (!address) return false;
  return Boolean(address.street1 || address.city || address.state || address.postalCode);
}

function pickBillingAddress(addresses: ContactAddressRow[]): BillToOverrideAddress | null {
  if (addresses.length === 0) return null;
  const chosen =
    addresses.find((a) => a.isBilling) ?? addresses.find((a) => a.isPrimary) ?? addresses[0];
  if (!chosen) return null;
  return {
    street1: chosen.street1 ?? null,
    street2: chosen.street2 ?? null,
    city: chosen.city ?? null,
    state: chosen.state ?? null,
    postalCode: chosen.postalCode ?? null,
    country: chosen.country ?? null,
  };
}

/**
 * A single expandable field row: at rest it shows the selected value legibly
 * (the `summary`), and expands to a Popover with the same options that used to
 * live in a separate picker + card. One control per field. (NOT-358)
 */
/**
 * A pickable value as an ordinary form field: label above, a select-look
 * trigger below, options in a popover. The customer section is built entirely
 * from these so everything changeable LOOKS changeable, in the same field
 * language as the sections under it — no edit modes, no inline chevrons to
 * hunt, no value that is secretly a button.
 */
/**
 * Where the work happens, for a customer that has more than one place.
 *
 * Its own control rather than the shared FieldPicker because the two needs it
 * has are exactly what that one lacks: a customer can own dozens of locations,
 * so the list needs a search, and an address is long, so the panel needs the
 * width the page actually has rather than the width of its trigger.
 */
/**
 * The people at the chosen location, folded away until asked for.
 *
 * Jobs only. A technician is going to this address and the office wants to see
 * — and often add — who they will meet, without leaving the form they are in
 * the middle of. Estimates and proposals do not ask: they are documents
 * addressed to the customer, and the people at a site are the location's
 * business, managed on the location itself.
 *
 * Collapsed by default because the common case is that the right people are
 * already there; the count in the summary is enough to say so.
 */
function LocationPeopleDisclosure({
  locationId,
  disabled = false,
}: {
  locationId: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const { data: people = [], isLoading } = useLocationContacts(locationId);

  // One row per person, however many roles they hold here.
  const byPerson = new Map<string, { name: string; roles: string[] }>();
  for (const person of people) {
    const name =
      person.contact?.displayName ||
      [person.contact?.firstName, person.contact?.lastName].filter(Boolean).join(' ') ||
      'Contact';
    const row = byPerson.get(person.contactId) ?? { name, roles: [] };
    row.roles.push(ROLE_LABEL[person.role] ?? person.role);
    byPerson.set(person.contactId, row);
  }
  const rows = [...byPerson.values()];

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex items-center gap-1 text-[13px] font-medium text-foreground/80 hover:text-foreground"
      >
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
        Contacts at this location
        <span className="text-muted-foreground">({isLoading ? '…' : rows.length})</span>
      </button>

      {open ? (
        <div className="mt-1.5 space-y-1.5 rounded-md border bg-muted/30 p-2.5">
          {isLoading ? (
            <p className="text-[13px] text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">
              Nobody named here — messages go to the account owner.
            </p>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.name} className="flex items-center gap-2 text-[13px]">
                  <User className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium text-foreground">{row.name}</span>
                  <span className="truncate text-muted-foreground">{row.roles.join(' and ')}</span>
                </li>
              ))}
            </ul>
          )}
          {!disabled ? (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1 text-[13px] font-medium text-primary hover:underline"
            >
              <UserPlus className="size-3.5" />
              Add contact
            </button>
          ) : null}
          <AddLocationContactDialog
            locationId={locationId}
            open={addOpen}
            onOpenChange={setAddOpen}
          />
        </div>
      ) : null}
    </div>
  );
}

function LocationPicker({
  value,
  total,
  addresses,
  indexOf,
  selectedIndex,
  disabled = false,
  onPick,
  onManual,
}: {
  value: ReactNode;
  total: number;
  addresses: EstimateAddress[];
  indexOf: (address: EstimateAddress) => number;
  selectedIndex: number | null;
  disabled?: boolean;
  onPick: (globalIndex: number) => void;
  onManual: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const term = query.trim().toLowerCase();
  const matches = term
    ? addresses.filter((address) =>
        [
          address.title,
          address.label,
          address.street,
          address.street2,
          address.city,
          address.state,
          address.zip,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term),
      )
    : addresses;

  return (
    <div className="min-w-0 space-y-1.5">
      <Label>Location</Label>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          // A stale filter is the worst thing to reopen onto: it reads as
          // "this customer only has one location".
          if (!next) setQuery('');
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-left text-sm transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="min-w-0 truncate">{value}</span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground/60" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          // As wide as the page allows, not as wide as the trigger: these are
          // street addresses, and truncating them defeats the picker.
          className="w-[min(46rem,calc(100vw-3rem))] p-0"
        >
          <div className="border-b p-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search this customer's locations by label, street, city or zip"
                className="h-9 pl-8"
              />
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto p-1.5">
            {matches.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                No location matches “{query.trim()}”.
              </p>
            ) : (
              <div className="grid gap-1 sm:grid-cols-2">
                {matches.map((address) => {
                  const globalIndex = indexOf(address);
                  const isSelected = globalIndex === selectedIndex;
                  return (
                    <button
                      key={globalIndex}
                      type="button"
                      onClick={() => {
                        onPick(globalIndex);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={cn(
                        'flex items-start gap-2 rounded-md px-2 py-2 text-left hover:bg-muted',
                        isSelected && 'bg-muted',
                      )}
                    >
                      <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0">
                        {formatContactAddressName(address) ? (
                          <span className="block truncate text-sm font-medium">
                            {formatContactAddressName(address)}
                          </span>
                        ) : null}
                        <span className="block truncate text-sm">
                          {formatContactAddressStreet(address)}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {formatContactAddressCity(address)}
                        </span>
                      </span>
                      {isSelected ? (
                        <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
            <span>
              {term ? `${matches.length} of ${total}` : `${total}`} locations on this customer
            </span>
            <button
              type="button"
              onClick={() => {
                onManual();
                setOpen(false);
                setQuery('');
              }}
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <PenLine className="size-3" />
              Enter one manually
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function FieldPicker({
  label,
  value,
  sub,
  disabled = false,
  children,
}: {
  label: string;
  value: ReactNode;
  /** A caveat about the current value (e.g. "Different from service address"). */
  sub?: ReactNode;
  disabled?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-w-0 space-y-1.5">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-left text-sm transition-colors hover:bg-muted/40 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
          >
            <span className="min-w-0 truncate">{value}</span>
            <ChevronDown className="size-4 shrink-0 text-muted-foreground/60" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-80 w-[var(--radix-popover-trigger-width)] min-w-72 overflow-y-auto p-1.5"
        >
          {children(() => setOpen(false))}
        </PopoverContent>
      </Popover>
      {sub ? <p className="text-[11.5px] leading-tight text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

function SummaryRow({
  disabled = false,
  align = 'center',
  summary,
  children,
}: {
  disabled?: boolean;
  align?: 'center' | 'start';
  summary: ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex w-full gap-3 rounded-md border border-input bg-background px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-60',
            align === 'start' ? 'items-start' : 'items-center',
          )}
        >
          <div className="min-w-0 flex-1">{summary}</div>
          <ChevronsUpDown
            className={cn('h-4 w-4 shrink-0 text-muted-foreground', align === 'start' && 'mt-0.5')}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-80 w-[var(--radix-popover-trigger-width)] min-w-72 overflow-y-auto p-1.5"
      >
        {children(() => setOpen(false))}
      </PopoverContent>
    </Popover>
  );
}

/** Shared option list rendered inside a {@link SummaryRow} / {@link MetaValue} popover. */
function OptionRows({
  options,
  value,
  onSelect,
}: {
  options: FieldSelectOption[];
  value: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div className="space-y-1">
      {options.map((option) => {
        const OptionIcon = option.icon;
        const selectedOption = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            onClick={() => {
              if (option.disabled) return;
              onSelect(option.value);
            }}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
              selectedOption
                ? 'bg-primary/10 text-primary'
                : option.tone === 'action'
                  ? 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  : 'hover:bg-muted',
              option.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {OptionIcon ? <OptionIcon className="h-3.5 w-3.5 shrink-0" /> : null}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{option.label}</span>
              {option.description ? (
                <span className="block truncate text-xs text-muted-foreground">
                  {option.description}
                </span>
              ) : null}
            </span>
            {selectedOption ? <Check className="h-3.5 w-3.5 shrink-0" /> : null}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Email/phone shown beneath the contact row: plain text when there is a single
 * value, a compact inline picker when the contact has several. (NOT-358)
 */
function MetaValue({
  icon: Icon,
  text,
  pickable,
  options,
  value,
  onSelect,
}: {
  icon: LucideIcon;
  text: string;
  pickable: boolean;
  options: FieldSelectOption[];
  value: string;
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  if (!pickable) {
    return (
      <span className="inline-flex min-w-0 items-center gap-1">
        <Icon className="h-3 w-3 shrink-0" />
        <span className="truncate">{text}</span>
      </span>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-w-0 items-center gap-1 text-primary hover:underline"
        >
          <Icon className="h-3 w-3 shrink-0" />
          <span className="truncate">{text}</span>
          <ChevronsUpDown className="h-3 w-3 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-80 w-72 overflow-y-auto p-1.5">
        <OptionRows
          options={options}
          value={value}
          onSelect={(next) => {
            onSelect(next);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** Rich address summary used as a {@link SummaryRow} trigger. */
function AddressTrigger({
  lines,
  badge,
  placeholder,
}: {
  lines: string[];
  badge?: string;
  placeholder: string;
}) {
  if (lines.length === 0) {
    return <span className="text-sm text-muted-foreground">{placeholder}</span>;
  }
  return (
    <div className="flex items-start gap-2">
      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        {lines.map((line, index) => (
          <p
            key={index}
            className={
              index === 0
                ? 'truncate font-medium text-foreground'
                : 'truncate text-xs text-muted-foreground'
            }
          >
            {line}
          </p>
        ))}
      </div>
      {badge ? (
        <span className="mt-0.5 shrink-0 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
          {badge}
        </span>
      ) : null}
    </div>
  );
}

function overrideAddressLines(address: BillToOverrideAddress | null): string[] {
  if (!address) return [];
  const cityLine = [[address.city, address.state].filter(Boolean).join(', '), address.postalCode]
    .filter(Boolean)
    .join(' ');
  return [[address.street1, address.street2].filter(Boolean).join(', '), cityLine]
    .map((line) => line?.trim())
    .filter((line): line is string => Boolean(line));
}

/**
 * A postal address, as an address is written.
 *
 * Street and unit on one line, then city, state and ZIP on the next — the two
 * lines an address has on an envelope, a work order and a map. It used to be
 * four stacked full-width boxes inside a bordered card, which read as four
 * unrelated questions sitting in a box of their own; the card also nested
 * inside the section's own card, so the manual address was a box in a box.
 *
 * Service and billing were two identical copies of this, differing only in the
 * id prefix. One definition, one prefix.
 */
/**
 * Which boxes on this section hold what Iris heard, and what she heard.
 *
 * One entry per BOX, not per idea: an address is a single thing the caller
 * said and five boxes on the form, and a call that yields "Vancouver, BC"
 * fills two of them. Marking the address as a whole would put her name on an
 * empty Street — a claim she never made — so the section is told, box by box,
 * exactly what is hers.
 *
 * `true` means she filled it but there is nothing useful to quote back.
 */
export type IrisFieldMarks = Partial<
  Record<'customerName' | 'customerPhone' | 'email' | AddressBox, IrisFieldMark>
>;

/** The boxes a postal address lands in. */
type AddressBox = 'street' | 'street2' | 'city' | 'state' | 'zip';

/**
 * A field label, with Iris's mark on it when the value in the box is hers.
 *
 * Every label that can be filled from a call goes through this, so the mark
 * always sits in the same place relative to the words — and so a label that
 * is not filled looks exactly as it always did.
 */
function IrisLabel({
  htmlFor,
  mark,
  children,
}: {
  htmlFor: string;
  mark?: IrisFieldMark;
  children: ReactNode;
}) {
  return (
    <Label htmlFor={htmlFor} className="gap-1.5">
      {children}
      <IrisMark mark={mark} />
    </Label>
  );
}

function AddressFields({
  prefix,
  street,
  street2,
  city,
  state,
  zip,
  onStreetChange,
  onStreet2Change,
  onCityChange,
  onStateChange,
  onZipChange,
  errors,
  showErrors,
  /** Per box — see {@link IrisFieldMarks}. */
  irisMarks,
}: {
  prefix: 'sa' | 'ba';
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  onStreetChange: (value: string) => void;
  onStreet2Change: (value: string) => void;
  onCityChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onZipChange: (value: string) => void;
  errors: AddressFieldErrors;
  showErrors: boolean;
  irisMarks?: IrisFieldMarks;
}) {
  const streetError = showErrors ? errors.street : undefined;
  return (
    <div className="grid grid-cols-1 gap-3">
      {/* The street takes what is left; a unit is "Apt 3B" and needs no more. */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
        <div className="min-w-0 space-y-1.5">
          <IrisLabel htmlFor={`${prefix}-street`} mark={irisMarks?.street}>
            Street <span className="text-destructive">*</span>
          </IrisLabel>
          <GooglePlacesInput
            id={`${prefix}-street`}
            className={cn(irisFieldClass(irisMarks?.street))}
            value={street}
            onChange={onStreetChange}
            ariaInvalid={Boolean(streetError)}
            ariaDescribedBy={streetError ? `${prefix}-street-error` : undefined}
            onAddressSelect={(parsed) => {
              onStreetChange(parsed.street);
              onStreet2Change(parsed.street2);
              onCityChange(parsed.city);
              onStateChange(parsed.state);
              onZipChange(parsed.zip);
            }}
          />
          <AddressFieldError id={`${prefix}-street-error`} message={streetError} />
        </div>
        <div className="min-w-0 space-y-1.5">
          <IrisLabel htmlFor={`${prefix}-street2`} mark={irisMarks?.street2}>
            Unit
          </IrisLabel>
          <Input
            id={`${prefix}-street2`}
            className={cn(irisFieldClass(irisMarks?.street2))}
            placeholder="Apt 3B"
            value={street2}
            onChange={(event) => onStreet2Change(event.target.value)}
          />
        </div>
      </div>
      <AddressLineTwoFields
        prefix={prefix}
        hideStreet2
        street2={street2}
        city={city}
        state={state}
        zip={zip}
        onStreet2Change={onStreet2Change}
        onCityChange={onCityChange}
        onStateChange={onStateChange}
        onZipChange={onZipChange}
        errors={errors}
        showErrors={showErrors}
        irisMarks={irisMarks}
      />
    </div>
  );
}

function AddressLineTwoFields({
  prefix,
  street2,
  city,
  state,
  zip,
  onStreet2Change,
  onCityChange,
  onStateChange,
  onZipChange,
  errors,
  showErrors,
  required = true,
  hideStreet2 = false,
  irisMarks,
}: {
  prefix: 'sa' | 'ba' | 'nc';
  street2: string;
  city: string;
  state: string;
  zip: string;
  onStreet2Change: (value: string) => void;
  onCityChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onZipChange: (value: string) => void;
  errors: AddressFieldErrors;
  showErrors: boolean;
  /** When false (the optional quick-create address), drop the required markers. */
  required?: boolean;
  /** The two-line layout puts the unit beside the street instead. */
  hideStreet2?: boolean;
  /** Per box — see {@link IrisFieldMarks}. */
  irisMarks?: IrisFieldMarks;
}) {
  const requiredMark = required ? <span className="text-destructive">*</span> : null;
  const cityError = showErrors ? errors.city : undefined;
  const stateError = showErrors ? errors.state : undefined;
  const zipError = showErrors ? errors.zip : undefined;
  return (
    <>
      {hideStreet2 ? null : (
        <div className="space-y-1.5">
          <IrisLabel htmlFor={`${prefix}-street2`} mark={irisMarks?.street2}>
            Street 2
          </IrisLabel>
          <Input
            id={`${prefix}-street2`}
            className={cn(irisFieldClass(irisMarks?.street2))}
            placeholder="Apt, suite, unit"
            value={street2}
            onChange={(event) => onStreet2Change(event.target.value)}
          />
        </div>
      )}
      {/* City on its own row, then State + ZIP paired: the service/billing
          address cards are a narrow (~half-column) container, so a single
          three-across row squeezed the City `1fr` track to 0 and overlapped its
          label (NOT-522). Stacking keeps every field legible at any width. */}
      {/* One line, proportioned: city takes what is left, state needs four
          characters, ZIP needs six. Below 640px they stack, which is the only
          width where stacking is right. */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_5.5rem_8rem]">
        <div className="min-w-0 space-y-1.5">
          <IrisLabel htmlFor={`${prefix}-city`} mark={irisMarks?.city}>
            City {requiredMark}
          </IrisLabel>
          <Input
            id={`${prefix}-city`}
            className={cn(irisFieldClass(irisMarks?.city))}
            placeholder="City"
            value={city}
            onChange={(event) => onCityChange(event.target.value)}
            aria-invalid={Boolean(cityError)}
            aria-describedby={cityError ? `${prefix}-city-error` : undefined}
          />
          <AddressFieldError id={`${prefix}-city-error`} message={cityError} />
        </div>
        <div className="contents">
          <div className="min-w-0 space-y-1.5">
            <IrisLabel htmlFor={`${prefix}-state`} mark={irisMarks?.state}>
              State {requiredMark}
            </IrisLabel>
            <Input
              id={`${prefix}-state`}
              className={cn(irisFieldClass(irisMarks?.state))}
              placeholder="TX"
              maxLength={2}
              pattern="[A-Za-z]{2}"
              title="Use a two-letter state or province code"
              autoCapitalize="characters"
              value={state}
              onChange={(event) => onStateChange(event.target.value)}
              aria-invalid={Boolean(stateError)}
              aria-describedby={stateError ? `${prefix}-state-error` : undefined}
            />
            <AddressFieldError id={`${prefix}-state-error`} message={stateError} />
          </div>
          <div className="min-w-0 space-y-1.5">
            <IrisLabel htmlFor={`${prefix}-zip`} mark={irisMarks?.zip}>
              ZIP {requiredMark}
            </IrisLabel>
            <Input
              id={`${prefix}-zip`}
              className={cn(irisFieldClass(irisMarks?.zip))}
              placeholder="78701"
              maxLength={POSTAL_CODE_MAX_LENGTH}
              value={zip}
              onChange={(event) => onZipChange(event.target.value)}
              aria-invalid={Boolean(zipError)}
              aria-describedby={zipError ? `${prefix}-zip-error` : undefined}
            />
            <AddressFieldError id={`${prefix}-zip-error`} message={zipError} />
          </div>
        </div>
      </div>
    </>
  );
}

function AddressFieldError({ id, message }: { id: string; message?: string }) {
  return message ? (
    <p id={id} className="text-xs leading-tight text-destructive" role="alert">
      {message}
    </p>
  ) : null;
}

function AddressValidationSummary({ message }: { message: string | null }) {
  return message ? (
    <p className="text-xs leading-tight text-destructive" role="alert">
      {message}
    </p>
  ) : null;
}

function formatContactAddressStreet(address: EstimateAddress) {
  return [address.street, address.street2].filter(Boolean).join(', ') || 'No street';
}

function formatContactAddressName(address: EstimateAddress | null) {
  if (!address) return '';
  const title = address.title?.trim();
  const label = address.label?.trim();
  return [title, label && label !== title ? label : null].filter(Boolean).join(' · ');
}

function formatContactAddressOption(address: EstimateAddress) {
  return [formatContactAddressName(address), formatContactAddressStreet(address)]
    .filter(Boolean)
    .join(' — ');
}

function formatContactAddressCity(address: EstimateAddress) {
  return [address.city, address.state, address.zip].filter(Boolean).join(', ');
}

function toSelectionAddress(address: EstimateAddress): ContactResultAddress {
  return {
    street: address.street,
    street2: address.street2 ?? undefined,
    city: address.city,
    state: address.state,
    zip: address.zip,
    isPrimary: address.isPrimary,
    isBilling: address.isBilling,
    isService: address.isService,
    accessNotes: address.accessNotes ?? undefined,
  };
}
